import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Plus, Search, Edit, Trash2, Tag, DollarSign, Calendar, TrendingDown, BarChart3, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Predefined category icons with visual emojis
const CATEGORY_ICONS = [
  { id: "salary", icon: "👨‍💼", label: "Gaji/Karyawan" },
  { id: "health", icon: "🏥", label: "Kesehatan/BPJS" },
  { id: "building", icon: "🏢", label: "Operasional" },
  { id: "electric", icon: "⚡", label: "Listrik" },
  { id: "phone", icon: "📱", label: "Telekomunikasi" },
  { id: "tools", icon: "🔧", label: "Peralatan" },
  { id: "router", icon: "📡", label: "Modem/ONT" },
  { id: "maintenance", icon: "🛠️", label: "Maintenance" },
  { id: "marketing", icon: "📢", label: "Marketing" },
  { id: "transport", icon: "🚗", label: "Transportasi" },
  { id: "document", icon: "📄", label: "Administrasi" },
  { id: "network", icon: "🌐", label: "Jaringan/ODP" },
  { id: "water", icon: "💧", label: "Air/PDAM" },
  { id: "food", icon: "🍽️", label: "Konsumsi" },
  { id: "rent", icon: "🏠", label: "Sewa" },
  { id: "tax", icon: "📋", label: "Pajak" },
  { id: "insurance", icon: "🛡️", label: "Asuransi" },
  { id: "training", icon: "📚", label: "Pelatihan" },
  { id: "other", icon: "📦", label: "Lainnya" },
];


const Expenses = () => {
  const { token, hasPermission, user } = useAuth();
  const { t, selectedBranch } = useApp();
  
  // Check if user is owner or kepala_cabang for branch filtering
  const isOwnerOrKepala = user?.role === 'owner' || user?.role === 'kepala_cabang';
  const userBranchId = user?.branch_id;
  
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'expense_date', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [expenseForm, setExpenseForm] = useState({
    branch_id: '',
    category_id: '',
    expense_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    payment_method: 'cash'
  });
  // Chart states
  const [chartData, setChartData] = useState(null);
  const [chartPeriod, setChartPeriod] = useState('monthly');
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [chartMonth, setChartMonth] = useState(new Date().getMonth() + 1);
  const [chartLoading, setChartLoading] = useState(false);


  const [coaExpenseOptions, setCoaExpenseOptions] = useState([]);
  const [categoryForm, setCategoryForm] = useState({
    category_name: '',
    account_code: '',
    description: '',
    icon: '📦',
    color: 'blue',
    sub_category: ''
  });

  useEffect(() => {
    if (token && hasPermission(['admin', 'kepala_cabang', 'owner'])) {
      fetchExpenses();
      fetchCategories();
      fetchBranches();
    }
  }, [token, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchChartData = async () => {
    try {
      setChartLoading(true);
      const params = new URLSearchParams({
        period: chartPeriod,
        year: chartYear.toString()
      });
      if (chartPeriod === 'monthly') {
        params.append('month', chartMonth.toString());
      }
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      
      const response = await axios.get(`${API_URL}/api/v2/expenses/chart-data?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setChartData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      toast.error('Gagal memuat data grafik');
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    if (token && hasPermission(['admin', 'kepala_cabang', 'owner'])) {
      fetchChartData();
    }
  }, [chartPeriod, chartYear, chartMonth, token, selectedBranch]);


  useEffect(() => {
    let filtered = expenses.filter(expense => {
      const category = categories.find(c => String(c.id) === String(expense.category_id));
      const matchesSearch = expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category?.category_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterPeriod === "month") {
        const expDate = new Date(expense.expense_date);
        return matchesSearch && expDate.getMonth() + 1 === selectedMonth && expDate.getFullYear() === selectedYear;
      } else if (filterPeriod === "year") {
        const expDate = new Date(expense.expense_date);
        return matchesSearch && expDate.getFullYear() === selectedYear;
      }
      return matchesSearch;
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'expense_date') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        } else if (sortConfig.key === 'category_name') {
          const catA = categories.find(c => String(c.id) === String(a.category_id));
          const catB = categories.find(c => String(c.id) === String(b.category_id));
          aValue = (catA?.category_name || a.category || '').toLowerCase();
          bValue = (catB?.category_name || b.category || '').toLowerCase();
        } else if (sortConfig.key === 'description') {
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredExpenses(filtered);
    setCurrentPage(1);
  }, [searchTerm, expenses, categories, sortConfig, filterPeriod, selectedMonth, selectedYear]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID').format(amount);
  };

  const formatInputAmount = (value) => {
    // Remove non-digit characters
    const number = value.replace(/\D/g, '');
    // Format with thousand separator
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const fetchExpenses = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      const response = await axios.get(`${API_URL}/api/v2/expenses?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data.data);
      setFilteredExpenses(response.data.data);
    } catch (error) {
      toast.error('Gagal memuat data pengeluaran');
    }
  };

  const fetchCategories = async () => {
    try {
      // Fetch COA expense options for category linking
      try {
        const coaRes = await axios.get(`${API_URL}/api/v2/accounting/coa/expense-options`, { headers: { Authorization: `Bearer ${token}` } });
        if (coaRes.data?.success) setCoaExpenseOptions(coaRes.data.data || []);
      } catch (e) { /* ignore */ }
      const response = await axios.get(`${API_URL}/api/expense-categories`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(response.data.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    try {
      const amountValue = parseFloat(expenseForm.amount.replace(/\./g, ''));
      
      if (editingExpense) {
        // Update existing expense
        await axios.put(
          `${API_URL}/api/v2/expenses/${editingExpense.id}`,
          {
            ...expenseForm,
            amount: amountValue,
            expense_date: new Date(expenseForm.expense_date).toISOString()
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Pengeluaran berhasil diupdate');
        setEditingExpense(null);
      } else {
        // Create new expense
        const payload = {
          ...expenseForm,
          amount: amountValue,
          expense_date: new Date(expenseForm.expense_date).toISOString(),
          created_by: user.id,
          branch_id: expenseForm.branch_id ? parseInt(expenseForm.branch_id) : null
        };
        await axios.post(
          `${API_URL}/api/v2/expenses`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Pengeluaran berhasil ditambahkan');
      }
      
      setIsExpenseDialogOpen(false);
      fetchExpenses();
      setExpenseForm({
        branch_id: '',
        category_id: '',
        expense_date: new Date().toISOString().split('T')[0],
        amount: '',
        description: '',
        payment_method: 'cash'
      });
    } catch (error) {
      toast.error('Gagal menambahkan pengeluaran');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      branch_id: expense.branch_id || '',
      category_id: expense.category_id,
      expense_date: new Date(expense.expense_date).toISOString().split('T')[0],
      amount: formatInputAmount(expense.amount.toString()),
      description: expense.description,
      payment_method: expense.payment_method
    });
    setIsExpenseDialogOpen(true);
  };

  const quickUpdateAccountCode = async (category, newAccountCode) => {
    try {
      await axios.put(
        `${API_URL}/api/expense-categories/${category.id}`,
        {
          category_name: category.category_name,
          description: category.description,
          icon: category.icon,
          color: category.color,
          sub_category: category.sub_category,
          account_code: newAccountCode || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Akun kategori diperbarui');
      fetchCategories();
    } catch (error) {
      console.error('Quick update error:', error);
      toast.error('Gagal memperbarui akun');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      category_name: category.category_name,
      account_code: category.account_code || '',
      description: category.description || '',
      icon: category.icon,
      color: category.color,
      sub_category: category.sub_category || ''
    });
    setIsCategoryDialogOpen(true);
  };

  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        // Update existing category
        await axios.put(
          `${API_URL}/api/expense-categories/${editingCategory.id}`,
          { ...categoryForm, is_active: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Kategori berhasil diupdate');
        setEditingCategory(null);
      } else {
        // Create new category
        await axios.post(
          `${API_URL}/api/expense-categories`,
          categoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Kategori berhasil ditambahkan');
      }
      
      setIsCategoryDialogOpen(false);
      fetchCategories();
      setCategoryForm({
        category_name: '',
        account_code: '',
        description: '',
        icon: '📦',
        color: 'blue',
        sub_category: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan kategori');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (window.confirm('Yakin ingin menghapus pengeluaran ini?')) {
      try {
        await axios.delete(`${API_URL}/api/v2/expenses/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pengeluaran berhasil dihapus');
        fetchExpenses();
      } catch (error) {
        toast.error('Gagal menghapus pengeluaran');
      }
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Yakin ingin menghapus kategori ini?')) {
      try {
        await axios.delete(`${API_URL}/api/expense-categories/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kategori berhasil dihapus');
        fetchCategories();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Gagal menghapus kategori');
      }
    }
  };

  const getCategoryColor = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      green: 'bg-green-100 text-green-700 border-green-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
      orange: 'bg-orange-100 text-orange-700 border-orange-200',
      amber: 'bg-amber-100 text-amber-700 border-amber-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      slate: 'bg-slate-100 text-slate-700 border-slate-200',
      pink: 'bg-pink-100 text-pink-700 border-pink-200',
      cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      gray: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[color] || colors.blue;
  };

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  if (!hasPermission(['admin', 'kepala_cabang', 'owner'])) {
    return (
      <Card className="border-0 shadow-lg dark:bg-slate-800">
        <CardContent className="py-12 text-center">
          <TrendingDown className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Anda tidak memiliki akses ke halaman ini</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 space-y-4 animate-fade-in p-4 w-full max-w-full overflow-x-hidden">
      {/* Summary Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-700 to-slate-800 text-white">
        <CardContent className="pt-3 pb-3 px-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs opacity-90 mb-0.5">Total Pengeluaran</p>
              <p className="text-2xl font-bold">Rp {formatCurrency(totalExpenses)}</p>
              <p className="text-[10px] opacity-75 mt-1">{filteredExpenses.length} transaksi</p>
            </div>
            <TrendingDown className="w-10 h-10 opacity-80" />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="expenses" className="space-y-3">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="expenses">Pengeluaran</TabsTrigger>
          <TabsTrigger value="grafik">Grafik</TabsTrigger>
          <TabsTrigger value="categories">Kategori</TabsTrigger>
        </TabsList>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-3">
          {/* Header Actions */}
          <Card className="border-0 shadow-sm dark:bg-slate-800">
            <CardContent className="pt-3 pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 dark:text-slate-500" />
                  <Input
                    placeholder="Cari pengeluaran..."
                    className="pl-10 bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 dark:text-white"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                {/* Filter Periode */}
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    className="px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-700 dark:text-white dark:border-slate-600"
                    value={filterPeriod}
                    onChange={(e) => setFilterPeriod(e.target.value)}
                  >
                    <option value="all">Semua</option>
                    <option value="month">Bulanan</option>
                    <option value="year">Tahunan</option>
                  </select>
                  
                  {filterPeriod === "month" && (
                    <>
                      <select
                        className="px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-700 dark:text-white dark:border-slate-600"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                      >
                        <option value={1}>Januari</option>
                        <option value={2}>Februari</option>
                        <option value={3}>Maret</option>
                        <option value={4}>April</option>
                        <option value={5}>Mei</option>
                        <option value={6}>Juni</option>
                        <option value={7}>Juli</option>
                        <option value={8}>Agustus</option>
                        <option value={9}>September</option>
                        <option value={10}>Oktober</option>
                        <option value={11}>November</option>
                        <option value={12}>Desember</option>
                      </select>
                      <select
                        className="px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-700 dark:text-white dark:border-slate-600"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      >
                        <option value={2024}>2024</option>
                        <option value={2025}>2025</option>
                        <option value={2026}>2026</option>
                      </select>
                    </>
                  )}
                  
                  {filterPeriod === "year" && (
                    <select
                      className="px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-700 dark:text-white dark:border-slate-600"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    >
                      <option value={2024}>2024</option>
                      <option value={2025}>2025</option>
                      <option value={2026}>2026</option>
                    </select>
                  )}
                </div>
                
                <Dialog open={isExpenseDialogOpen} onOpenChange={(open) => {
                  setIsExpenseDialogOpen(open);
                  if (!open) {
                    setEditingExpense(null);
                    setExpenseForm({
                      branch_id: '',
                      category_id: '',
                      expense_date: new Date().toISOString().split('T')[0],
                      amount: '',
                      description: '',
                      payment_method: 'cash'
                    });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800">
                      <Plus className="w-4 h-4 mr-2" />
                      Tambah Pengeluaran
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="dark:bg-slate-800">
                    <DialogHeader>
                      <DialogTitle className="dark:text-white">
                        {editingExpense ? 'Edit Pengeluaran' : 'Tambah Pengeluaran Baru'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmitExpense} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="dark:text-white">Kategori</Label>
                        <select
                          required
                          className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white"
                          value={expenseForm.category_id}
                          onChange={(e) => setExpenseForm({...expenseForm, category_id: e.target.value})}
                        >
                          <option value="">Pilih Kategori</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.icon} {cat.category_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="dark:text-white">Tanggal</Label>
                          <Input
                            type="date"
                            required
                            value={expenseForm.expense_date}
                            onChange={(e) => {
                              // 2026-07-29: Auto-sync Bulan (description) mengikuti Tanggal.
                              // Format tanggal input = YYYY-MM-DD.
                              const dateStr = e.target.value;
                              const monthNames = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
                              let autoMonth = expenseForm.description;
                              if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                                const monthIdx = parseInt(dateStr.slice(5,7), 10) - 1;
                                if (monthIdx >= 0 && monthIdx <= 11) {
                                  autoMonth = monthNames[monthIdx];
                                }
                              }
                              setExpenseForm({...expenseForm, expense_date: dateStr, description: autoMonth});
                            }}
                            className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                            data-testid="expense-tanggal-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="dark:text-white">Jumlah (Rp)</Label>
                          <Input
                            type="text"
                            required
                            value={expenseForm.amount}
                            onChange={(e) => setExpenseForm({...expenseForm, amount: formatInputAmount(e.target.value)})}
                            placeholder="1.000.000"
                            className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="dark:text-white">Bulan</Label>
                        <select
                          required
                          value={expenseForm.description}
                          onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                          className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white"
                          data-testid="expense-bulan-select"
                        >
                          <option value="">— Pilih Bulan —</option>
                          <option value="Januari">Januari</option>
                          <option value="Februari">Februari</option>
                          <option value="Maret">Maret</option>
                          <option value="April">April</option>
                          <option value="Mei">Mei</option>
                          <option value="Juni">Juni</option>
                          <option value="Juli">Juli</option>
                          <option value="Agustus">Agustus</option>
                          <option value="September">September</option>
                          <option value="Oktober">Oktober</option>
                          <option value="November">November</option>
                          <option value="Desember">Desember</option>
                        </select>
                      </div>
                      {/* Branch selector - only for admin */}
                      {!isOwnerOrKepala && (
                        <div className="space-y-2">
                          <Label className="dark:text-white">Cabang</Label>
                          <select
                            className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white"
                            value={expenseForm.branch_id}
                            onChange={(e) => setExpenseForm({...expenseForm, branch_id: e.target.value})}
                          >
                            <option value="">Pilih Cabang</option>
                            {branches.map(branch => (
                              <option key={branch.id} value={branch.id}>{branch.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {isOwnerOrKepala && userBranchId && (
                        <div className="space-y-2">
                          <Label className="dark:text-white">Cabang</Label>
                          <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300">
                            {branches.find(b => b.id === userBranchId)?.name || 'Cabang Anda'}
                          </div>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="dark:text-white">Metode Pembayaran</Label>
                        <select
                          className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white"
                          value={expenseForm.payment_method}
                          onChange={(e) => setExpenseForm({...expenseForm, payment_method: e.target.value})}
                        >
                          <option value="cash">Tunai</option>
                          <option value="transfer">Transfer</option>
                          <option value="card">Kartu</option>
                        </select>
                      </div>
                      <div className="flex justify-end space-x-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsExpenseDialogOpen(false)}>
                          Batal
                        </Button>
                        <Button type="submit" className="bg-gradient-to-r from-red-600 to-red-700">
                          Simpan
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>

          {/* Expenses Table */}
          <Card className="border-0 shadow-sm dark:bg-slate-800">
            <CardContent className="p-0">
              {/* Pagination Controls */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 dark:text-slate-300">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-xs text-slate-600 dark:text-slate-300">entries</span>
                </div>
                <span className="text-xs text-slate-600 dark:text-slate-300">
                  Total: {filteredExpenses.length} pengeluaran
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600">
                    <tr>
                      <th 
                        className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                        onClick={() => handleSort('expense_date')}
                      >
                        <div className="flex items-center gap-1">
                          Tanggal
                          {sortConfig.key === 'expense_date' && (
                            <span className="text-[10px]">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                        onClick={() => handleSort('category_name')}
                        data-testid="sort-kategori"
                      >
                        <div className="flex items-center gap-1">
                          Kategori
                          {sortConfig.key === 'category_name' && (
                            <span className="text-[10px]">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                        onClick={() => handleSort('description')}
                        data-testid="sort-deskripsi"
                      >
                        <div className="flex items-center gap-1">
                          Deskripsi
                          {sortConfig.key === 'description' && (
                            <span className="text-[10px]">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                        onClick={() => handleSort('amount')}
                      >
                        <div className="flex items-center gap-1">
                          Jumlah
                          {sortConfig.key === 'amount' && (
                            <span className="text-[10px]">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Cabang</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Metode</th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((expense, index) => {
                      const category = categories.find(c => String(c.id) === String(expense.category_id));
                      const expenseDate = new Date(expense.expense_date);
                      
                      return (
                        <tr key={index} className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                          <td className="py-2 px-3">
                            <div className="flex items-center space-x-1 text-xs text-slate-600 dark:text-slate-300">
                              <Calendar className="w-3 h-3" />
                              <span>{format(expenseDate, 'dd MMM yyyy')}</span>
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            {category && (
                              <Badge variant="outline" className={`${getCategoryColor(category.color)} text-[10px] py-1 px-2 inline-flex items-center gap-1 min-w-[150px] justify-start`}>
                                <span>{category.icon}</span>
                                <span>{category.category_name}</span>
                              </Badge>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs text-slate-700 dark:text-slate-200">
                            {expense.description}
                          </td>
                          <td className="py-2 px-3 text-left font-bold text-xs text-red-600 dark:text-red-400">
                            Rp {formatCurrency(expense.amount)}
                          </td>
                          <td className="py-2 px-3">
                            <Badge variant="outline" className="text-[10px] py-1 px-2 inline-flex items-center justify-start min-w-[80px] bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              {expense.branch_name || '-'}
                            </Badge>
                          </td>
                          <td className="py-2 px-3">
                            <Badge variant="secondary" className="capitalize text-[10px] py-1 px-2 inline-flex items-center justify-start min-w-[80px]">
                              {expense.payment_method === 'cash' ? 'Tunai' : 
                               expense.payment_method === 'transfer' ? 'Transfer' : 
                               expense.payment_method === 'card' ? 'Kartu' : 
                               expense.payment_method}
                            </Badge>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex justify-center space-x-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                onClick={() => handleEditExpense(expense)}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                onClick={() => handleDeleteExpense(expense.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredExpenses.length > itemsPerPage && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 dark:border-slate-700">
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredExpenses.length)} of {filteredExpenses.length} entries
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="h-7 px-2 text-xs"
                    >
                      Previous
                    </Button>
                    {Array.from({ length: Math.ceil(filteredExpenses.length / itemsPerPage) }, (_, i) => i + 1)
                      .filter(page => {
                        return page === 1 || 
                               page === Math.ceil(filteredExpenses.length / itemsPerPage) || 
                               Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, array) => {
                        if (index > 0 && array[index - 1] !== page - 1) {
                          return (
                            <React.Fragment key={`ellipsis-${page}`}>
                              <span className="px-2 py-1 text-xs">...</span>
                              <Button
                                size="sm"
                                variant={currentPage === page ? "default" : "outline"}
                                onClick={() => setCurrentPage(page)}
                                className="h-7 w-7 p-0 text-xs"
                              >
                                {page}
                              </Button>
                            </React.Fragment>
                          );
                        }
                        return (
                          <Button
                            key={page}
                            size="sm"
                            variant={currentPage === page ? "default" : "outline"}
                            onClick={() => setCurrentPage(page)}
                            className="h-7 w-7 p-0 text-xs"
                          >
                            {page}
                          </Button>
                        );
                      })}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredExpenses.length / itemsPerPage), prev + 1))}
                      disabled={currentPage === Math.ceil(filteredExpenses.length / itemsPerPage)}
                      className="h-7 px-2 text-xs"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        
        {/* Grafik Tab */}
        <TabsContent value="grafik" className="space-y-4">
          <Card className="border-0 shadow-sm dark:bg-slate-800">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-800 dark:text-white">Grafik Pengeluaran</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Analisis pengeluaran berdasarkan kategori</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={chartPeriod}
                    onChange={(e) => setChartPeriod(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                  >
                    <option value="monthly">Bulanan</option>
                    <option value="yearly">Tahunan</option>
                  </select>
                  <Input
                    type="number"
                    value={chartYear}
                    onChange={(e) => setChartYear(parseInt(e.target.value))}
                    className="w-24 h-9 text-sm dark:bg-slate-700 dark:text-white dark:border-slate-600"
                  />
                  {chartPeriod === 'monthly' && (
                    <select
                      value={chartMonth}
                      onChange={(e) => setChartMonth(parseInt(e.target.value))}
                      className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                    >
                      <option value={1}>Januari</option>
                      <option value={2}>Februari</option>
                      <option value={3}>Maret</option>
                      <option value={4}>April</option>
                      <option value={5}>Mei</option>
                      <option value={6}>Juni</option>
                      <option value={7}>Juli</option>
                      <option value={8}>Agustus</option>
                      <option value={9}>September</option>
                      <option value={10}>Oktober</option>
                      <option value={11}>November</option>
                      <option value={12}>Desember</option>
                    </select>
                  )}
                  <Button onClick={fetchChartData} variant="outline" size="sm" className="border-red-600 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30">
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {chartLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : chartData ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800">
                  <CardContent className="pt-4 pb-4 px-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Total Pengeluaran</p>
                      <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
                        <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">Rp {formatCurrency(chartData.total_expenses)}</p>
                    <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Periode: {chartData.date_range?.start} - {chartData.date_range?.end}</p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800">
                  <CardContent className="pt-4 pb-4 px-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Jumlah Kategori</p>
                      <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                        <Tag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-slate-800 dark:text-white">{chartData.category_breakdown?.length || 0}</p>
                    <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Kategori dengan transaksi</p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800">
                  <CardContent className="pt-4 pb-4 px-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Kategori Terbesar</p>
                      <div className="p-2 bg-orange-50 dark:bg-orange-900/30 rounded-lg">
                        <BarChart3 className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      </div>
                    </div>
                    <p className="text-lg font-bold text-slate-800 dark:text-white truncate">
                      {chartData.top_categories?.[0]?.icon} {chartData.top_categories?.[0]?.name || '-'}
                    </p>
                    <p className="text-xs text-red-500 mt-1">Rp {formatCurrency(chartData.top_categories?.[0]?.amount || 0)}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800">
                  <CardHeader className="border-b border-slate-200 dark:border-slate-700 py-3 px-4">
                    <CardTitle className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      {chartPeriod === 'yearly' ? 'Tren Pengeluaran per Bulan' : 'Tren Pengeluaran per Hari'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData.trend_data || []} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(value) => value >= 1000000 ? (value / 1000000).toFixed(0) + 'jt' : (value / 1000).toFixed(0) + 'rb'} />
                        <Tooltip formatter={(value) => ['Rp ' + Number(value).toLocaleString('id-ID'), 'Pengeluaran']} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                        <Legend />
                        <Bar dataKey="Pengeluaran" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800">
                  <CardHeader className="border-b border-slate-200 dark:border-slate-700 py-3 px-4">
                    <CardTitle className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Pengeluaran per Kategori
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={chartData.category_breakdown || []} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, percent }) => name + ': ' + (percent * 100).toFixed(0) + '%'} labelLine={false}>
                          {(chartData.category_breakdown || []).map((entry, index) => (
                            <Cell key={'cell-' + index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => ['Rp ' + Number(value).toLocaleString('id-ID'), '']} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800">
                <CardHeader className="border-b border-slate-200 dark:border-slate-700 py-3 px-4">
                  <CardTitle className="text-sm font-semibold text-slate-800 dark:text-white">Top Kategori Pengeluaran</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 dark:bg-slate-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300">Kategori</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300">Jumlah Transaksi</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300">Total</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300">Persentase</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {(chartData.category_breakdown || []).map((cat, index) => {
                          const percentage = chartData.total_expenses > 0 ? ((cat.value / chartData.total_expenses) * 100).toFixed(1) : 0;
                          return (
                            <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                              <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300"><span className="mr-2">{cat.icon || '📦'}</span>{cat.name}</td>
                              <td className="px-4 py-2.5 text-sm text-right text-slate-600 dark:text-slate-400">{cat.count || 0} transaksi</td>
                              <td className="px-4 py-2.5 text-sm text-right font-medium text-red-600 dark:text-red-400">Rp {formatCurrency(cat.value)}</td>
                              <td className="px-4 py-2.5 text-sm text-right text-slate-600 dark:text-slate-400">{percentage}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-100 dark:bg-slate-600">
                        <tr>
                          <td className="px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-200">Total</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-slate-600 dark:text-slate-300">{(chartData.category_breakdown || []).reduce((sum, c) => sum + (c.count || 0), 0)} transaksi</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-red-600 dark:text-red-400">Rp {formatCurrency(chartData.total_expenses)}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-slate-600 dark:text-slate-300">100%</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-0 shadow-lg dark:bg-slate-800">
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">Tidak ada data grafik</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-3">
          <Card className="border-0 shadow-sm dark:bg-slate-800">
            <CardContent className="pt-3 pb-3">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-800 dark:text-white">Kategori Pengeluaran</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{categories.length} kategori</p>
                </div>
                {hasPermission(['admin']) && (
                  <Dialog open={isCategoryDialogOpen} onOpenChange={(open) => {
                    setIsCategoryDialogOpen(open);
                    if (!open) {
                      setEditingCategory(null);
                      setCategoryForm({
                        category_name: '',
                        account_code: '',
                        description: '',
                        icon: '📦',
                        color: 'blue',
                        sub_category: ''
                      });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-indigo-600 to-purple-600">
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Kategori
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="dark:bg-slate-800">
                      <DialogHeader>
                        <DialogTitle className="dark:text-white">
                          {editingCategory ? 'Edit Kategori' : 'Tambah Kategori Baru'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmitCategory} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label className="dark:text-white">Nama Kategori</Label>
                          <Input
                            required
                            value={categoryForm.category_name}
                            onChange={(e) => setCategoryForm({...categoryForm, category_name: e.target.value})}
                            placeholder="Contoh: Biaya Listrik"
                            className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="dark:text-white">Sub Kategori</Label>
                          <Input
                            value={categoryForm.sub_category}
                            onChange={(e) => setCategoryForm({...categoryForm, sub_category: e.target.value})}
                            placeholder="Contoh: Listrik Kantor"
                            className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="dark:text-white">Akun (Chart of Accounts) <span className="text-xs text-slate-500 font-normal dark:text-slate-400">— untuk Laba Rugi otomatis</span></Label>
                          <select
                            className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white text-sm"
                            value={categoryForm.account_code || ''}
                            onChange={(e) => setCategoryForm({...categoryForm, account_code: e.target.value})}
                            data-testid="category-akun-select"
                          >
                            <option value="">— Belum dipilih (tidak masuk Laba Rugi) —</option>
                            {coaExpenseOptions.map(opt => (
                              <option key={opt.code} value={opt.code}>{opt.code} - {opt.name}</option>
                            ))}
                          </select>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Pilih akun beban yang akan tampil di Laporan Laba Rugi untuk kategori ini.</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="dark:text-white">Deskripsi</Label>
                          <Input
                            value={categoryForm.description}
                            onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                            placeholder="Deskripsi singkat kategori"
                            className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                          />
                        </div>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="dark:text-white">Pilih Icon</Label>
                            <div className="grid grid-cols-6 gap-2 p-2 border rounded-lg bg-slate-50 dark:bg-slate-700 max-h-32 overflow-y-auto">
                              {CATEGORY_ICONS.map((item) => (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => setCategoryForm({...categoryForm, icon: item.icon})}
                                  className={`p-2 text-2xl rounded-lg transition-all hover:bg-slate-200 dark:hover:bg-slate-600 ${categoryForm.icon === item.icon ? "bg-indigo-100 dark:bg-indigo-900 ring-2 ring-indigo-500" : ""}`}
                                  title={item.label}
                                >
                                  {item.icon}
                                </button>
                              ))}
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Icon terpilih: {categoryForm.icon}</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="dark:text-white">Warna</Label>
                            <select
                              className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white"
                              value={categoryForm.color}
                              onChange={(e) => setCategoryForm({...categoryForm, color: e.target.value})}
                            >
                              <option value="blue">Blue</option>
                              <option value="green">Green</option>
                              <option value="purple">Purple</option>
                              <option value="orange">Orange</option>
                              <option value="amber">Amber</option>
                              <option value="indigo">Indigo</option>
                              <option value="pink">Pink</option>
                              <option value="cyan">Cyan</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>
                            Batal
                          </Button>
                          <Button type="submit" className="bg-gradient-to-r from-indigo-600 to-purple-600">
                            Simpan
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => {
                  const categoryExpenses = expenses.filter(e => String(e.category_id) === String(category.id));
                  const categoryTotal = categoryExpenses.reduce((sum, e) => sum + e.amount, 0);
                  
                  return (
                    <Card key={category.id} className="border-0 shadow card-hover dark:bg-slate-700">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="text-3xl">{category.icon}</div>
                            <div>
                              <h4 className="font-semibold text-slate-800 dark:text-white">
                                {category.category_name}
                              </h4>
                              {category.sub_category && (
                                <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-medium">
                                  {category.sub_category}
                                </p>
                              )}
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {category.description}
                              </p>
                              {/* Inline Akun (COA) - badge + dropdown when needed */}
                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] text-slate-500 dark:text-slate-400">Akun:</span>
                                {category.account_code ? (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700" data-testid={`category-akun-badge-${category.id}`}>
                                    <span>{category.account_code}</span>
                                    <span className="text-emerald-500">·</span>
                                    <span className="font-sans">{(coaExpenseOptions.find(o => o.code === category.account_code)?.name) || ''}</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded border border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">⚠ Belum dipilih</span>
                                )}
                                <select
                                  value={category.account_code || ''}
                                  onChange={(e) => quickUpdateAccountCode(category, e.target.value)}
                                  className="text-[11px] px-1.5 py-0.5 border border-slate-200 dark:border-slate-500 rounded bg-white dark:bg-slate-600 dark:text-white"
                                  data-testid={`category-akun-inline-${category.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  title="Pilih/Ubah Akun COA"
                                >
                                  <option value="">— Pilih akun —</option>
                                  {coaExpenseOptions.map(opt => (
                                    <option key={opt.code} value={opt.code}>{opt.code} - {opt.name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                          {hasPermission(['admin']) && (
                            <div className="flex space-x-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                                onClick={() => handleEditCategory(category)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                                onClick={() => handleDeleteCategory(category.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              {categoryExpenses.length} transaksi
                            </span>
                            <span className="font-bold text-red-600 dark:text-red-400">
                              {formatCurrency(categoryTotal)}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Expenses;
